import {
  AfterViewInit, Component, ElementRef, EventEmitter,
  Input, OnInit, OnDestroy, Output, Pipe, PipeTransform, ViewChild
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Observable, Subject} from 'rxjs';
import {map, startWith, takeUntil} from 'rxjs/operators';


@Pipe({
    name: 'filterByString',
    pure: true,
    standalone: false
})
export class FilterByString implements PipeTransform {
  transform(strList: any[], filter: any): any {
    if (!Array.isArray(strList)) return [];
    if (!filter) return strList;

    const filterValue = filter.toString().toLowerCase();

    return strList.filter(item =>
        item != null && item.toString().toLowerCase().includes(filterValue)
    );
  }
}

@Component({
    selector: 'app-search-admin-field',
    templateUrl: './search-admin-field.component.html',
    styleUrls: ['./search-admin-field.component.scss'],
    standalone: false
})
export class SearchAdminFieldComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() options: string[] = [];
  @Input() dataStructure: any;
  @Input() ariaLabel: string = '';
  @Input() professional: boolean = false;
  @Input() searchPlaceholder: string = '';
  @Input() loading: boolean = false;

  @Output() onSearch = new EventEmitter();
  @Output() onChange = new EventEmitter();
  @Output() onClick = new EventEmitter();

  @Input() myModel!: any;
  @Output() myModelChange = new EventEmitter<any>();

  /** When true, the component allows multiple selections shown as chips */
  @Input() multiSelect: boolean = false;

  myControl = new FormControl('');
  filteredOptions!: Observable<string[]>;
  private _disabled = false;

  private destroy$ = new Subject<void>();

  /**
   * Guard flag: when true the next blur was caused by an autocomplete
   * selection that was already handled — skip the blur logic.
   */
  private justSelected = false;

  /**
   * Blur validation is deferred: pressing the mouse on an option blurs the
   * input BEFORE the click selects it. Validating immediately cleared the
   * typed search text, which re-filtered the list under the cursor, so the
   * click landed on a different element and nothing was selected
   * (scrolling worked, search-then-pick didn't).
   */
  private blurValidateTimer?: number;

  /** Last value emitted to the parent — avoids duplicate change events. */
  private lastEmitted: any = undefined;

  /** Tracks selected labels in multi-select mode */
  selectedItems: string[] = [];

  /** Reference to the multi-select input so we can refocus after selection */
  @ViewChild('multiInput') multiInput!: ElementRef;

  /** Reference to the autocomplete trigger so we can reopen the panel */
  @ViewChild('multiInput', { read: MatAutocompleteTrigger }) multiAutoTrigger!: MatAutocompleteTrigger;

  /** Single-select input/trigger, used to keep the field visible above mobile keyboards. */
  @ViewChild('singleInput') singleInput?: ElementRef<HTMLInputElement>;
  @ViewChild('singleInput', { read: MatAutocompleteTrigger }) singleAutoTrigger?: MatAutocompleteTrigger;

  private mobileFocusedInput: HTMLElement | null = null;
  private mobileViewportListening = false;
  private mobileBaselineViewportHeight = 0;
  private mobileViewportTimer?: number;
  private mobileBlurTimer?: number;
  private readonly mobileViewportHandler = () => this.syncMobileViewport();
  autocompletePosition: 'auto' | 'above' | 'below' = 'auto';

  constructor() {}

  // ─── Initialisation from parent ───────────────────────────────────

  /**
   * Accept pre-selected labels for multi-select mode.
   * Called by the parent to restore previously saved selections.
   */
  @Input()
  set initialSelectedItems(items: string[]) {
    if (items && items.length) {
      this.selectedItems = [...items];
    }
  }

  @Input()
  set setOptions(options: any[]) {
    this.options = (options || []).map(itm => itm?.label ?? '');
    this.rebuildFilteredOptions();
  }

  get selectedLable() {
    if (!this.options || !this.myModel) return null;
    return this.options.find(opt => opt === this.myModel);
  }

  get selectedValue() {
    return this.selectedLable;
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    if (this.myControl) {
      value == true ? this.myControl.disable() : this.myControl.enable();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  ngOnInit() {
    this.rebuildFilteredOptions();
  }

  ngAfterViewInit() {
    if (!this.multiSelect && this.selectedLable) {
      this.myControl.setValue(this.selectedLable);
    }
  }

  ngOnDestroy() {
    window.clearTimeout(this.blurValidateTimer);
    this.stopMobileViewportTracking(true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Helpers ──────────────────────────────────────────────────────

  /** Single place that wires up the filteredOptions observable */
  private rebuildFilteredOptions(): void {
    this.filteredOptions = this.myControl.valueChanges.pipe(
        startWith(this.myControl.value || ''),
        map(value => this._filter(value || '')),
        takeUntil(this.destroy$)
    );
  }

  private _filter(value: string): string[] {
    if (!value || !value.toString().trim()) {
      return this.options;
    }

    const filterValue = value.toString().toLowerCase();

    return this.options.filter(option =>
        option && option.toLowerCase().includes(filterValue)
    );
  }

  /** Returns true if a label is currently selected (multi-select mode) */
  isSelected(option: string): boolean {
    return this.selectedItems.includes(option);
  }

  // ─── Event handlers ───────────────────────────────────────────────

  onClickFunc() {
    this.onClick.emit();
  }

  onInputFocus(input: HTMLElement): void {
    this.startMobileViewportTracking(input);
    this.onClickFunc();
  }

  onMobileInputBlur(): void {
    if (typeof window === 'undefined') return;
    window.clearTimeout(this.mobileBlurTimer);
    this.mobileBlurTimer = window.setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      const anotherPublishInputFocused = Boolean(
          activeElement?.closest('app-ui-select-dropdown') &&
          (activeElement.matches('input') || activeElement.getAttribute('role') === 'combobox')
      );

      this.stopMobileViewportTracking(!anotherPublishInputFocused);
    }, 220);
  }

  /**
   * Called when the user picks an option from the autocomplete panel.
   *
   * Single-select: fires BEFORE blur → emit and guard.
   * Multi-select:  toggles the item, clears the input, reopens the panel.
   */
  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;

    if (this.multiSelect) {
      // ── Toggle selection ──
      const idx = this.selectedItems.indexOf(value);
      if (idx >= 0) {
        this.selectedItems.splice(idx, 1);
      } else {
        this.selectedItems.push(value);
      }

      // Clear the search text so the full list is visible again
      this.myControl.setValue('');

      // Emit the current array of selected labels
      this.onChange.emit([...this.selectedItems]);
      this.myModelChange.emit([...this.selectedItems]);

      // Keep the panel open so the user can continue selecting
      setTimeout(() => {
        this.multiInput?.nativeElement?.focus();
        this.multiAutoTrigger?.openPanel();
      });
    } else {
      // ── Single-select ──
      this.justSelected = true;
      window.clearTimeout(this.blurValidateTimer);
      this.lastEmitted = value;
      this.onChange.emit(value);
      this.myModelChange.emit(value);

      // A single selection is complete: dismiss the software keyboard so
      // the user immediately regains the full form viewport.
      setTimeout(() => {
        this.singleInput?.nativeElement?.blur();
        this.stopMobileViewportTracking(true);
      });
    }
  }

  /**
   * Remove a chip in multi-select mode.
   */
  removeItem(item: string): void {
    const idx = this.selectedItems.indexOf(item);
    if (idx >= 0) {
      this.selectedItems.splice(idx, 1);
      this.onChange.emit([...this.selectedItems]);
      this.myModelChange.emit([...this.selectedItems]);
    }
  }

  /**
   * Called on input blur (single-select only — multi-select input
   * doesn't bind blur to avoid interfering with chip interactions).
   */
  onValueChange() {
    window.clearTimeout(this.blurValidateTimer);
    // Give a pending option click time to land (mousedown → blur → click).
    this.blurValidateTimer = window.setTimeout(() => this.validateTypedValue(), 200);
  }

  private validateTypedValue(): void {
    if (this.justSelected) {
      this.justSelected = false;
      return;
    }
    // Focus came back to the input (e.g. user clicked into it again) — still editing.
    if (this.singleInput && document.activeElement === this.singleInput.nativeElement) {
      return;
    }

    const currentValue = this.myControl.value;
    const selectedCategory = this.options.find(opt => opt === currentValue);

    if (!selectedCategory) {
      this.myControl.setValue('');
    }

    // Skip re-emitting the option that was just picked (the post-select blur).
    if (selectedCategory && selectedCategory === this.lastEmitted) return;
    this.lastEmitted = selectedCategory;
    this.onChange.emit(selectedCategory);
    this.myModelChange.emit(selectedCategory);
  }

  onMySearch($event: KeyboardEvent) {
    this.onSearch.emit(this.myControl.value);
  }

  clearSelection(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.justSelected = true;
    window.clearTimeout(this.blurValidateTimer);
    this.lastEmitted = undefined;
    this.myControl.setValue('');
    this.myModel = undefined;
    this.onChange.emit(undefined);
    this.myModelChange.emit(undefined);
    this.onSearch.emit('');
  }

  private startMobileViewportTracking(input: HTMLElement): void {
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches) {
      return;
    }

    window.clearTimeout(this.mobileBlurTimer);
    this.mobileFocusedInput = input;
    this.mobileBaselineViewportHeight = Math.max(
        this.mobileBaselineViewportHeight,
        window.visualViewport?.height ?? window.innerHeight
    );
    // Keep the interaction visually natural: input first, options immediately
    // below it, then the software keyboard. syncMobileViewport() creates the
    // required space by moving the input upward as the keyboard animates in.
    this.autocompletePosition = 'below';
    document.body.classList.add('publish-keyboard-active');

    const viewport = window.visualViewport;
    if (viewport && !this.mobileViewportListening) {
      viewport.addEventListener('resize', this.mobileViewportHandler, {passive: true});
      viewport.addEventListener('scroll', this.mobileViewportHandler, {passive: true});
      this.mobileViewportListening = true;
    }

    // Mobile keyboards animate in after focus. Sync at the start and again
    // after the animation settles instead of guessing one fixed scroll offset.
    this.syncMobileViewport();
    window.clearTimeout(this.mobileViewportTimer);
    this.mobileViewportTimer = window.setTimeout(() => this.syncMobileViewport(), 320);
  }

  private syncMobileViewport(): void {
    if (!this.mobileFocusedInput || typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const keyboardHeight = Math.max(
        0,
        window.innerHeight - viewportHeight - viewportTop,
        this.mobileBaselineViewportHeight - viewportHeight
    );
    const keyboardOpen = keyboardHeight > 90;
    const safeGap = 18;
    const inputRect = this.mobileFocusedInput.getBoundingClientRect();

    document.body.classList.toggle('publish-keyboard-open', keyboardOpen);
    document.documentElement.style.setProperty('--publish-keyboard-height', `${keyboardHeight}px`);

    let scrollDelta = 0;
    let predictedSpaceBelow = viewportBottom - inputRect.bottom - safeGap;

    if (keyboardOpen) {
      // Reserve roughly half of the reduced viewport for options. Cap the
      // movement so the input never disappears above the visible viewport.
      const desiredPanelHeight = Math.min(260, Math.max(170, viewportHeight * .5));
      const requiredSpace = Math.max(0, desiredPanelHeight - predictedSpaceBelow);
      const maximumUpwardMovement = Math.max(0, inputRect.top - (viewportTop + safeGap));
      scrollDelta = Math.min(requiredSpace, maximumUpwardMovement);
      predictedSpaceBelow += scrollDelta;
    } else if (inputRect.bottom > viewportBottom - safeGap) {
      scrollDelta = inputRect.bottom - (viewportBottom - safeGap);
      predictedSpaceBelow += scrollDelta;
    } else if (inputRect.top < viewportTop + safeGap) {
      scrollDelta = inputRect.top - (viewportTop + safeGap);
    }

    const panelHeight = Math.max(120, Math.min(300, predictedSpaceBelow));
    document.documentElement.style.setProperty('--publish-dropdown-max-height', `${panelHeight}px`);

    if (Math.abs(scrollDelta) > 2) {
      // Keyboard resize emits several frames. An immediate adjustment avoids
      // stacked smooth-scroll animations that make the field jump or oscillate.
      window.scrollBy({top: scrollDelta, behavior: keyboardOpen ? 'auto' : 'smooth'});
    }

    window.requestAnimationFrame(() => {
      this.singleAutoTrigger?.updatePosition();
      this.multiAutoTrigger?.updatePosition();
    });
  }

  private stopMobileViewportTracking(clearGlobalState: boolean): void {
    if (typeof window === 'undefined') return;

    window.clearTimeout(this.mobileViewportTimer);
    window.clearTimeout(this.mobileBlurTimer);

    const viewport = window.visualViewport;
    if (viewport && this.mobileViewportListening) {
      viewport.removeEventListener('resize', this.mobileViewportHandler);
      viewport.removeEventListener('scroll', this.mobileViewportHandler);
    }

    this.mobileViewportListening = false;
    this.mobileFocusedInput = null;
    this.mobileBaselineViewportHeight = 0;
    this.autocompletePosition = 'auto';

    if (clearGlobalState) {
      document.body.classList.remove('publish-keyboard-active', 'publish-keyboard-open');
      document.documentElement.style.removeProperty('--publish-keyboard-height');
      document.documentElement.style.removeProperty('--publish-dropdown-max-height');
    }
  }
}
